import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Coins, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MODEL_LABELS = {
  'nano-banana': 'Nano Banana (Gemini) — standard',
  'gpt-image-1': 'GPT Image 1 — premium (AI Poster)',
  'dall-e-3': 'DALL·E 3 — premium',
};

/**
 * Admin control for how many monthly image credits each engine consumes.
 * GPT Image 1 / DALL·E 3 cost ~6x Nano Banana, so weighting them protects margin.
 */
export const ImageCreditWeightsPanel = ({ api }) => {
  const [weights, setWeights] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/admin/settings/image-credits');
      setWeights(r.data.weights);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/settings/image-credits', { weights });
      setWeights(r.data.weights);
      toast.success('Image credit weights saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="image-credits-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Coins className="w-5 h-5 text-amber-600" /> Image Credit Weights</CardTitle>
        <CardDescription>
          How many of a plan's monthly AI-image credits each engine consumes. GPT Image 1 costs ~6x Nano Banana —
          weighting it protects per-subscriber margin. (1–20 each.)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!weights ? (
          <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
        ) : (
          <div className="space-y-3">
            {Object.keys(MODEL_LABELS).map((m) => (
              <div key={m} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-50">
                <Label className="text-sm">{MODEL_LABELS[m]}</Label>
                <Input type="number" min={1} max={20} value={weights[m] ?? 1}
                  onChange={(e) => setWeights((w) => ({ ...w, [m]: Math.max(1, Math.min(20, Number(e.target.value) || 1)) }))}
                  className="w-20 text-center" data-testid={`image-credit-${m}`} />
              </div>
            ))}
            <Button onClick={save} disabled={saving} data-testid="save-image-credits-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save weights
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageCreditWeightsPanel;
